import { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    RadioGroup,
    List,
    ListItem,
    ListItemButton,
    FormControlLabel,
    Radio,
    CircularProgress,
    Modal,
    Box,
} from "@mui/material";

import { create, InstanceProps } from "react-modal-promise";
import { IQuote } from "src/Interfaces/quotes";
import { ICountryData, ISetCustomerData } from "@interfaces";
import moment from "moment";
import { Form, Formik, FormikHelpers } from "formik";
import { AddTravellerFormComponent } from "src/Components/AddTravellerComponent/AddTravellerFormComponent";
import _ from "lodash";
import { StyledButton } from "@components";
import { createNewCartCustomerSchema } from "@validations";
import { BOOKING_DATE_FORMAT } from "@constants";
import styled from "styled-components";
import { DiscountInfoModal } from "./DiscountInfoModal";

interface IConfirmModalProps extends InstanceProps<boolean, boolean> {
    isOpen: boolean;
    message: string;
    onResolve: (confirmed?: boolean) => void;
    onReject: () => void;
}

export const ConfirmModal = ({ isOpen, message, onResolve }: IConfirmModalProps) => {
    return (
        <Dialog open={isOpen} onClose={() => onResolve()}>
            <DialogTitle>Confirm</DialogTitle>
            <DialogContent>{message}</DialogContent>
            <DialogActions>
                <Button onClick={() => onResolve()} color="secondary">
                    Cancel
                </Button>
                <Button onClick={() => onResolve(true)} color="primary">
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const confirmModal = create<IConfirmModalProps>(ConfirmModal);

interface ICreateNewCustomerModalProps extends InstanceProps<ISetCustomerData, string> {
    isOpen: boolean;
    onResolve: (customerData?: ISetCustomerData) => void;
    onReject: () => void;
    loadCountries: () => Promise<ICountryData[]>;
}

export const CreateNewCustomerModal = ({
    isOpen,
    onResolve,
    onReject,
    loadCountries,
}: ICreateNewCustomerModalProps) => {
    const [countriesData, setCountriesData] = useState<ICountryData[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                setIsLoading(true);
                const countries = await loadCountries();
                setCountriesData(countries);
            } catch (error) {
                console.error("Failed to load countries:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (isOpen) {
            fetchCountries();
        }
    }, [isOpen, loadCountries]);

    const initialValues: ISetCustomerData = {
        title: "Mr", // Set a default title if needed
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        postalCode: "",
        countryCode: "",
        dateOfBirth: moment().format("YYYY-MM-DD"),
        customerIndex: 0,
    };

    const handleSubmit = async (
        values: ISetCustomerData,
        { setSubmitting, validateForm }: FormikHelpers<ISetCustomerData>,
    ) => {
        try {
            const validationResult = await validateForm();
            if (!_.isEmpty(validationResult)) {
                return;
            }
            setSubmitting(true);
            const formattedValues = {
                ...values,
                dateOfBirth: moment(values.dateOfBirth).format(BOOKING_DATE_FORMAT),
            };
            await onResolve(formattedValues);
        } catch (error) {
            console.error("Submission error:", error);
            onReject();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={isOpen}
            onClose={() => onResolve()}
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "auto",
                padding: "16px",
            }}
        >
            {isLoading ? (
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            ) : (
                <Box sx={{ width: "90vw", maxWidth: 700 }}>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={createNewCartCustomerSchema}
                        onSubmit={handleSubmit}
                    >
                        {({
                            values,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                            isSubmitting,
                            isValid,
                        }) => (
                            <StyledForm>
                                <Header>
                                    <p className="heading">New Customer</p>
                                </Header>
                                <AddTravellerFormComponent
                                    values={values}
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                    handleSubmit={handleSubmit}
                                    dateValue={moment(values.dateOfBirth).toDate()}
                                    countriesData={countriesData}
                                    handleChangeDate={(date) =>
                                        setFieldValue(
                                            "dateOfBirth",
                                            date ? moment(date).format("YYYY-MM-DD") : "",
                                        )
                                    }
                                    isShowErrorDate={false}
                                />
                                <StyledButton
                                    className="saveButton"
                                    color={["#436CB2 0%", "#28B7C3 100%"]}
                                    $borderRadius="4px"
                                    type="submit"
                                    disabled={!isValid || isSubmitting}
                                >
                                    {isSubmitting ? "Saving ..." : "Save"}
                                </StyledButton>
                            </StyledForm>
                        )}
                    </Formik>
                </Box>
            )}
        </Modal>
    );
};

export const createNewCustomerModal = create<ICreateNewCustomerModalProps>(CreateNewCustomerModal);

interface ICreateNewQuoteModalProps extends InstanceProps<string, string> {
    isOpen: boolean;
    onResolve: (title?: string) => void;
    onReject: () => void;
}

export const CreateNewQuoteModal = ({ isOpen, onResolve }: ICreateNewQuoteModalProps) => {
    const [quoteTitle, setQuoteTitle] = useState("");

    return (
        <Dialog open={isOpen} onClose={() => onResolve()}>
            <DialogTitle>Create New Quote</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Quote Title"
                    fullWidth
                    variant="outlined"
                    value={quoteTitle}
                    onChange={(e) => setQuoteTitle(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onResolve()} color="secondary">
                    Cancel
                </Button>
                <Button
                    onClick={() => onResolve(quoteTitle)}
                    color="primary"
                    disabled={!quoteTitle.trim()}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const createNewQuoteModal = create<ICreateNewQuoteModalProps>(CreateNewQuoteModal);

interface IAddToExistingQuoteModalProps extends InstanceProps<IQuote, void> {
    isOpen: boolean;
    onResolve: (quote?: IQuote) => void;
    onReject: () => void;
    quotes: IQuote[];
}

export const AddToExistingQuoteModal = ({
    isOpen,
    onResolve,
    quotes,
}: IAddToExistingQuoteModalProps) => {
    const [selectedQuote, setSelectedQuote] = useState<IQuote | null>(null);

    return (
        <Dialog open={isOpen} onClose={() => onResolve()}>
            <DialogTitle>Select an Existing Quote</DialogTitle>
            <DialogContent>
                <FormControl component="fieldset">
                    <RadioGroup
                        value={selectedQuote?.id || ""}
                        onChange={(e) => {
                            const quote = quotes.find((q) => q.id.toString() === e.target.value);
                            setSelectedQuote(quote || null);
                        }}
                    >
                        <List>
                            {quotes.map((quote) => (
                                <ListItem key={quote.id} disablePadding>
                                    <ListItemButton onClick={() => setSelectedQuote(quote)}>
                                        <FormControlLabel
                                            value={quote.id.toString()}
                                            control={<Radio />}
                                            label={quote.title}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </RadioGroup>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onResolve()} color="secondary">
                    Cancel
                </Button>
                <Button
                    onClick={() => onResolve(selectedQuote!)}
                    color="primary"
                    disabled={!selectedQuote}
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const addToExistingQuoteModal = create(AddToExistingQuoteModal);

interface IDiscountInfoModalProps extends InstanceProps<void, void> {
    isOpen: boolean;
    percent: number;
    learnMoreHref: string;
    imageUrl?: string;
    onResolve: () => void;
    onReject: () => void;
}

export const DiscountInfoModalPromise = create<IDiscountInfoModalProps>((props) => (
    <DiscountInfoModal
        isOpen={props.isOpen}
        onClose={props.onResolve}
        percent={props.percent}
        learnMoreHref={props.learnMoreHref}
        imageUrl={props.imageUrl}
    />
));

const StyledForm = styled(Form)`
    padding: 30px 16px;
    background-color: #fff;
    border-radius: 8px;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    .heading {
        font-size: 28px;
        font-weight: 700;
    }
`;
